// The attendance roll (lane B7 S14; CR-070, plan D12): the required attendees are the Recruiter, the Hiring Manager,
// the HR Business Partner and every Final Interview Loop interviewer; the roll groups them as *Required* (in the room)
// and *Missing attendee* (DC-182). Written input is evidence on the sheet and never counts as attendance, so a person
// with written input who is not in the room stays a Missing attendee, the server's own attendance words under the row.
// The Coordinator and oversight readers appear as observers with *No vote* / *Oversight*, and read the roll only.

import { useState } from "react";
import { ACTION_LABELS, AttendanceRoll, Button, useHs, type RollAttendee, type RollObserver } from "@/mc";
import { ROOM_RULES } from "./labels";
import { ROLE_WORD } from "./Readiness";
import type { Attendee } from "./types";

export type AttendanceMark = { person_id: string; status: "present" | "absent_with_written_input" | "absent" };

/** The marks to record from the roll as ticked: an absent person with written input keeps that status. */
export function marksOf(attendees: readonly Attendee[], present: Readonly<Record<string, boolean>>): AttendanceMark[] {
  return attendees.map((a) => ({
    person_id: a.person_id,
    status: present[a.person_id] ? "present" : a.has_written_input ? "absent_with_written_input" : "absent",
  }));
}

export function rollAttendees(attendees: readonly Attendee[], present: Readonly<Record<string, boolean>>): RollAttendee[] {
  return attendees.map((a, index) => ({
    key: a.person_id,
    name: a.name,
    seat: ROLE_WORD[a.role] ?? a.role,
    hueKey: a.person_id,
    present: !!present[a.person_id],
    reason: present[a.person_id] ? undefined : a.attendance_label ?? undefined,
    hs: `room.roll.${index}`,
  }));
}

export interface RollProps {
  attendees: readonly Attendee[];
  observers?: readonly RollObserver[] | undefined;
  /** The roll is being taken and this person may record it (`can.record_attendance`). */
  editable: boolean;
  /** The status pill (Roll Call while the roll is taken). */
  status?: string | undefined;
  onSave?: ((marks: AttendanceMark[]) => void) | undefined;
  busy?: boolean | undefined;
}

export function Roll({ attendees, observers, editable, status, onSave, busy }: RollProps) {
  const [present, setPresent] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(attendees.map((a) => [a.person_id, a.status === "present"])));
  const hs = useHs("room.roll");
  return (
    <div className="mc-stack" {...hs}>
      <AttendanceRoll
        attendees={rollAttendees(attendees, present)}
        observers={observers}
        status={status}
        mode={editable ? "edit" : "view"}
        onToggle={editable ? (key, on) => setPresent((was) => ({ ...was, [key]: on })) : undefined}
      />
      {editable ? (
        <>
          <Button tone="primary" sm busy={busy}  onClick={() => onSave?.(marksOf(attendees, present))}>
            {ACTION_LABELS.save}
          </Button>
          <p className="mc-hint">{ROOM_RULES.requiredAttendees}</p>
        </>
      ) : null}
    </div>
  );
}
